import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { httpRetryer } from '@frontend/util/http-utilities';
import { AuthService } from 'auth';
import { EnvService } from 'frontend-environment';
import {
  applicationManagerMessages,
  applicationManagerService,
} from 'grpc-gateway-generated';
import {
  EMPTY,
  expand,
  from,
  iif,
  map,
  Observable,
  of,
  reduce,
  switchMap,
  take,
} from 'rxjs';

export enum ConfigType {
  STRING = 'STRING',
  BOOLEAN = 'BOOLEAN',
  INT = 'INT',
  MAP = 'MAP',
}

export interface Config {
  configKind: ConfigKind;
  configValue: ConfigValue;
  fc: FormControl;
}
export interface ConfigKind {
  uuid: string;
  type: ConfigType;
  name: string;
  displayName: string;
  description: string;
  allowedCouType: string;
  extensions: string[];
}

export interface ConfigValue {
  configKindUuid: string;
  cou: { claim: string; value: string };
  createdAt: string;
  uuid: string;
  value: {
    isBool: boolean;
    isInt: boolean;
    isMap: boolean;
    isString: boolean;
    valueBool: boolean;
    valueInt: number;
    valueMap: {};
    valueString: string;
  };
}
@Injectable({
  providedIn: 'root',
})
export class ApplicationManagerApiService {
  private env = inject(
    EnvService<{
      APPLICATION_MANAGER_URL: string;
      COU_CONFIG_URL: string;
    }>,
  );
  private authService = inject(AuthService);
  private httpClient = inject(HttpClient);

  listAllCustomers(status = 'ANY') {
    const listCustomers = (continuationToken?: string) => {
      // actualUserHubIdpToken$ is used because we need a non-impersonated access token
      return this.authService.actualApplicationManagerIdpToken$.pipe(
        take(1),
        switchMap((token) =>
          from(
            applicationManagerService.ApplicationManagerApiProcess.ListCustomers(
              continuationToken ? { continuationToken } : { query: { status } },
              {
                pathPrefix: this.env.APPLICATION_MANAGER_URL,
                headers: { authorization: `Bearer ${token}` },
              },
            ),
          ).pipe(httpRetryer({})),
        ),
      );
    };

    return listCustomers().pipe(
      // expands the observable to get all follow up pages
      expand((res) =>
        iif(
          (): boolean => !!res.continuationToken,
          listCustomers(res.continuationToken),
          EMPTY,
        ),
      ),
      // merges all the results, like a reduce, starting with an empty array
      reduce(
        (acc, res) => acc.concat(res.customers || []),
        <applicationManagerMessages.Customer[]>[],
      ),
      map((customers) =>
        customers.map((customer) => ({
          ...customer,
          createdDate: customer.createdDate
            ? new Date(customer.createdDate)
            : customer.createdDate,
        })),
      ),
    );
  }

  /** if a customer is stored, return it otherwise fetch */
  getCustomerById(
    customerId: string,
  ): Observable<applicationManagerMessages.CustomerResponse> {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        from(
          applicationManagerService.ApplicationManagerApiProcess.GetCustomer(
            {
              customerId,
            },
            {
              pathPrefix: this.env.APPLICATION_MANAGER_URL,
              headers: { authorization: `Bearer ${authToken}` },
            },
          ),
        ).pipe(httpRetryer({})),
      ),
    );
  }

  createCustomer(
    customer: applicationManagerMessages.ProvisionCustomerRequest,
  ) {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        from(
          applicationManagerService.ApplicationManagerApiProcess.ProvisionCustomer(
            {
              ...customer,
            },
            {
              pathPrefix: this.env.APPLICATION_MANAGER_URL,
              headers: { authorization: `Bearer ${authToken}` },
            },
          ),
        ).pipe(httpRetryer({})),
      ),
    );
  }

  editCustomer(customer: applicationManagerMessages.UpdateCustomerRequest) {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        from(
          applicationManagerService.ApplicationManagerApiProcess.UpdateCustomer(
            {
              ...customer,
            },
            {
              pathPrefix: this.env.APPLICATION_MANAGER_URL,
              headers: { authorization: `Bearer ${authToken}` },
            },
          ),
        ).pipe(httpRetryer({})),
      ),
    );
  }

  listAllCustomerUsers(customerId: string) {
    const listCustomerUsers = (continuationToken?: string) => {
      // actualUserHubIdpToken$ is used because we need a non-impersonated access token
      return this.authService.actualApplicationManagerIdpToken$.pipe(
        take(1),
        switchMap((token) =>
          from(
            applicationManagerService.ApplicationManagerApiProcess.ListUsers(
              continuationToken
                ? { continuationToken }
                : { query: { customerId: customerId } },
              {
                pathPrefix: this.env.APPLICATION_MANAGER_URL,
                headers: { authorization: `Bearer ${token}` },
              },
            ),
          ).pipe(httpRetryer({})),
        ),
      );
    };

    return listCustomerUsers().pipe(
      // expands the observable to get all follow up pages
      expand((res) =>
        iif(
          (): boolean => !!res.continuationToken,
          listCustomerUsers(res.continuationToken),
          EMPTY,
        ),
      ),
      // merges all the results, like a reduce, starting with an empty array
      reduce(
        (acc, res) => acc.concat(res.users || []),
        <applicationManagerMessages.User[]>[],
      ),
    );
  }

  createCustomerAppInstance(
    instance: applicationManagerMessages.ProvisionInstanceRequest,
  ) {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        from(
          applicationManagerService.ApplicationManagerApiProcess.ProvisionInstance(
            {
              ...instance,
            },
            {
              pathPrefix: this.env.APPLICATION_MANAGER_URL,
              headers: { authorization: `Bearer ${authToken}` },
            },
          ),
        ).pipe(httpRetryer({})),
      ),
    );
  }

  getConfigKinds() {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        this.httpClient
          .post<{ results: ConfigKind[] }>(
            `${this.env.COU_CONFIG_URL}/v1/get-config-kinds`,
            {},
            {
              headers: { authorization: `Bearer ${authToken}` },
            },
          )
          .pipe(httpRetryer({})),
      ),
    );
  }

  getConfig(uuid: string) {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        this.httpClient
          .post<{ results: ConfigValue[] }>(
            `${this.env.COU_CONFIG_URL}/v1/get-cou-configs`,
            {
              cou: {
                claim: 'sub.mar',
                value: uuid,
              },
            },
            {
              headers: { authorization: `Bearer ${authToken}` },
            },
          )
          .pipe(httpRetryer({})),
      ),
    );
  }

  setConfig(configValue: ConfigValue) {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        this.httpClient
          .post<{ results: ConfigValue[] }>(
            `${this.env.COU_CONFIG_URL}/v1/set-cou-config`,
            configValue,
            {
              headers: { authorization: `Bearer ${authToken}` },
            },
          )
          .pipe(httpRetryer({})),
      ),
    );
  }

  unsetConfig(uuid: string) {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        this.httpClient
          .post<{ results: ConfigValue[] }>(
            `${this.env.COU_CONFIG_URL}/v1/unset-cou-config`,
            { uuid },
            {
              headers: { authorization: `Bearer ${authToken}` },
            },
          )
          .pipe(httpRetryer({})),
      ),
    );
  }

  decommissionInstance(instance: applicationManagerMessages.Instance) {
    throw new Error('Method not implemented.');
    return of();
    // TODO: No api yet
    // return this.authService.actualApplicationManagerIdpToken$.pipe(
    //   take(1),
    //   switchMap((authToken) =>
    //     from(
    //       applicationManagerService.ApplicationManagerApiProcess.ProvisionInstance(
    //         {
    //           instanceId: instance.instanceId,
    //         },
    //         {
    //           pathPrefix: this.env.APPLICATION_MANAGER_URL,
    //           headers: { authorization: `Bearer ${authToken}` },
    //         },
    //       ),
    //     ).pipe(httpRetryer({})),
    //   ),
    // );
  }

  toggleUserAccess(userId: number, instanceId: string, hasAccess: boolean) {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        from(
          applicationManagerService.ApplicationManagerApiProcess.ToggleUserAccess(
            {
              userId,
              instanceId,
              hasAccess,
            },
            {
              pathPrefix: this.env.APPLICATION_MANAGER_URL,
              headers: { authorization: `Bearer ${authToken}` },
            },
          ),
        ).pipe(httpRetryer({})),
      ),
    );
  }

  addUsers(customerId: string, users: applicationManagerMessages.User[]) {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        from(
          applicationManagerService.ApplicationManagerApiProcess.AddUsers(
            {
              customerId,
              users,
            },
            {
              pathPrefix: this.env.APPLICATION_MANAGER_URL,
              headers: { authorization: `Bearer ${authToken}` },
            },
          ),
        ).pipe(httpRetryer({})),
      ),
    );
  }

  toggleUserAdmin(customerId: string, userId: number, isAdmin: boolean) {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        from(
          applicationManagerService.ApplicationManagerApiProcess.ToggleUserAdminAccess(
            {
              customerId,
              userId,
              isAdmin,
            },
            {
              pathPrefix: this.env.APPLICATION_MANAGER_URL,
              headers: { authorization: `Bearer ${authToken}` },
            },
          ),
        ).pipe(httpRetryer({})),
      ),
    );
  }

  toggleUserInternalAdmin(
    customerId: string,
    userId: number,
    isInternal: boolean,
  ) {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        from(
          applicationManagerService.ApplicationManagerApiProcess.ToggleInternalAdminAccess(
            {
              customerId,
              userId,
              isInternal,
            },
            {
              pathPrefix: this.env.APPLICATION_MANAGER_URL,
              headers: { authorization: `Bearer ${authToken}` },
            },
          ),
        ).pipe(httpRetryer({})),
      ),
    );
  }

  updateUser(userId: number, email: string, name: string) {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        from(
          applicationManagerService.ApplicationManagerApiProcess.UpdateUser(
            {
              userId,
              email,
              name,
            },
            {
              pathPrefix: this.env.APPLICATION_MANAGER_URL,
              headers: { authorization: `Bearer ${authToken}` },
            },
          ),
        ).pipe(httpRetryer({})),
      ),
    );
  }

  toggleDisabledUser(customerId: string, userId: number, isDisabled: boolean) {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        from(
          applicationManagerService.ApplicationManagerApiProcess.ToggleDisabledUser(
            {
              customerId,
              userId,
              isDisabled,
            },
            {
              pathPrefix: this.env.APPLICATION_MANAGER_URL,
              headers: { authorization: `Bearer ${authToken}` },
            },
          ),
        ).pipe(httpRetryer({})),
      ),
    );
  }

  listPolicies() {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        from(
          applicationManagerService.ApplicationManagerApiProcess.ListPolicies(
            {},
            {
              pathPrefix: this.env.APPLICATION_MANAGER_URL,
              headers: { authorization: `Bearer ${authToken}` },
            },
          ),
        ).pipe(httpRetryer({})),
      ),
    );
  }

  upsertPolicy(policyId: string, name: string) {
    return this.authService.actualApplicationManagerIdpToken$.pipe(
      take(1),
      switchMap((authToken) =>
        from(
          applicationManagerService.ApplicationManagerApiProcess.UpsertPolicy(
            {
              policyId,
              name,
            },
            {
              pathPrefix: this.env.APPLICATION_MANAGER_URL,
              headers: { authorization: `Bearer ${authToken}` },
            },
          ),
        ).pipe(httpRetryer({})),
      ),
    );
  }
}
